import classNames from 'classnames/bind';
import React, { FC, forwardRef } from 'react';
import { Color } from 'components/colors';
import styles from './progress-pie.module.css';

const pixels = {
  small: 14,
  default: 18,
  large: 26,
};

/**
 * Color to be used for the progress status (pie), to denote the status.
 */
const ProgressColor = {
  default: styles.progress_color,
  success: styles.progress_color__success,
  warn: styles.progress_color__warn,
  error: styles.progress_color__danger,
  inProgress: styles.progress_color__progress,
  disabled: styles.progress_color__disabled,
  special: styles.progress_color__special,
};

/**
 * Color to be filled in the progress background, to denote the status.
 * NOTE :: This overrides inner progress bar section
 */
const FillColor = {
  default: styles.fill_color,
  success: styles.fill_color__success,
  warn: styles.fill_color__warn,
  error: styles.fill_color__danger,
  inProgress: styles.fill_color__progress,
  disabled: styles.fill_color__disabled,
  special: styles.fill_color__special,
};

type ProgressPieProps = {
  className?: string;
  progress?: number;
  size?: keyof typeof pixels;
  type?: 'default' | 'success' | 'warn' | 'error' | 'disabled';
  backgroundFill?: boolean;
  color?: Color;
};

/**
 * @description Progess component to show the progress in Pie format
 * Use fill to show as the bubble with border and BG color; But cannot show progress, It is false by default
 * In other cases, it can be a progress bar.
 */
export const ProgressPie: FC<ProgressPieProps> = forwardRef<
  any,
  ProgressPieProps
>(
  (
    {
      className,
      progress = 0,
      size = 'default',
      type = 'default',
      backgroundFill,
      color,
    },
    ref,
  ) => {
    const cx = classNames.bind(styles);
    const progressClassName = cx(styles.pie, className);
    const rValue = pixels[size] - 2;
    const viewBox = rValue * 2;
    const stroke = color
      ? `var(--ozone-${color}-background)`
      : ProgressColor[type];
    const fillColor = color ? `var(--ozone-${color}-surface)` : FillColor[type];

    return (
      <svg
        ref={ref}
        className={progressClassName}
        viewBox={`0 0 ${viewBox} ${viewBox}`}
        width={pixels[size]}
        style={{
          backgroundColor: backgroundFill ? fillColor : 'transparent',
          borderColor: stroke,
        }}
      >
        <circle
          r={rValue}
          cx={rValue}
          cy={rValue}
          style={{
            stroke: progress ? stroke : '', // to remove the small vertical line over background fill in safari
            strokeDasharray: `${progress} 100`,
          }}
        />
      </svg>
    );
  },
);
